import React, { useEffect, useState } from 'react';
import Comment from './Comment';
import { MDBTypography } from "mdb-react-ui-kit";
import { useDict } from '../../../contexts/LanguageContext';
import { getComments, postSubmitComment } from '../../investmentNote/src/investmentNoteApis';
import DeleteCommentModal from '../../investmentNote/views/DeleteCommentModal';

function CommentBoard({ investmentNoteId }) {
    const [comments, setComments] = useState([]);
    const [commentInput, setCommentInput] = useState('');
    const [replyInput, setReplyInput] = useState('');
    const [isDeleteCommentModalOpen, setIsDeleteCommentModalOpen] = useState(false);
    const [deleteCommentRequest, setDeleteCommentRequest] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const dict = useDict();

    useEffect(() => {
        getComments(investmentNoteId, handleGetComments);
    }, []);

    const handleGetComments = (response) => {
        const { data: responseData } = response.data;
        setComments(responseData);
    };

    const handleCloseDeleteCommentModal = () => {
        setIsDeleteCommentModalOpen(false);
    };

    const handleCommentSubmit = async (commentId) => {
        setIsLoading(true);

        const content = commentInput;
        const parent_comment_id = commentId;
        const investment_note_id = investmentNoteId;

        const payload = {
            content,
            investment_note_id,
            parent_comment_id,
        };

        await postSubmitComment(payload, handlePostComment);

        setIsLoading(false);
    };

    const handleReplySubmit = async (commentId) => {
        setIsLoading(true);

        const content = replyInput;
        const parent_comment_id = commentId;
        const investment_note_id = investmentNoteId;

        const payload = {
            content,
            investment_note_id,
            parent_comment_id,
        };

        await postSubmitComment(payload, handlePostComment);

        setIsLoading(false);
    };

    const handlePostComment = (response) => {
        setReplyInput('');
        setCommentInput('');

        addComment(response);
    };

    const addComment = (response) => {
        setComments((prevComments) => {
            if (response.parent_comment_id === null) {
                return [...prevComments, response.comment];
            }

            // Recursively find the parent comment in the comments tree
            const findAndAddComment = (comments) => {
                return comments.map((comment) => {
                    // If the current comment matches the parent ID, add the new comment as its child
                    if (comment.id === response.parent_comment_id) {
                        return {
                            ...comment,
                            descendants: [...(comment.descendants || []), response.comment],
                        };
                    }
                    // If the current comment has children, recursively search for the parent comment
                    if (comment.descendants) {
                        return {
                            ...comment,
                            descendants: findAndAddComment(comment.descendants),
                        };
                    }
                    return comment;
                });
            };

            // Copy the comments tree and update the state with the modified structure
            return findAndAddComment(prevComments);
        });
    };

    const handleDeleteComment = (commentId) => {
        const comment_id = commentId;

        const payload = {
            comment_id,
        };

        setIsDeleteCommentModalOpen(true);
        setDeleteCommentRequest(payload);
    };

    const deleteComment = (commentId) => {
        setComments((prevComments) => {
            // Recursively find and remove the comment with the specified ID
            const findAndDeleteComment = (comments) => {
                return comments.reduce((updatedComments, comment) => {
                    // If the current comment matches the ID, skip it (effectively deleting it)
                    if (comment.id === commentId) {
                        return updatedComments;
                    }
                    // If the current comment has children, recursively search for the comment to delete
                    if (comment.descendants) {
                        return [...updatedComments, { ...comment, descendants: findAndDeleteComment(comment.descendants) }];
                    }
                    // If the current comment does not match the ID and has no children, keep it
                    return [...updatedComments, comment];
                }, []);
            };

            // Copy the comments tree and update the state with the modified structure
            return findAndDeleteComment(prevComments);
        });
    };

    return (
        <>
            <section className="bg-light">
                <MDBTypography className="text-dark text-bold font-size-18 text-center pt-3">
                    {dict.comment}
                </MDBTypography>
                <Comment
                    comments={comments}
                    onCommentInputChange={setCommentInput}
                    onSubmit={(commentId) => handleCommentSubmit(commentId)}
                    onReplyInputChange={setReplyInput}
                    replyInput={replyInput}
                    onReplySubmit={(commentId) => handleReplySubmit(commentId)}
                    onDelete={handleDeleteComment}
                    isLoading={isLoading}
                />
                <DeleteCommentModal
                    isOpen={isDeleteCommentModalOpen}
                    setIsOpen={setIsDeleteCommentModalOpen}
                    handleClose={handleCloseDeleteCommentModal}
                    deleteCommentRequest={deleteCommentRequest}
                    deleteComment={(commentId) => deleteComment(commentId)}
                />
            </section>
        </>
    );
}

export default CommentBoard;
